import { ButtonCusWithIcon } from '@/components/common/Button';
import TableActionButtons from '@/components/common/TableActionButtons';
import TableCustomOnlyTwoDept from '@/components/table/custom/TableCustomOnlyTwoDept';
import {
    DEFAULT_SELECT_PAGE_SIZES,
    DISPPLAY_MOMENT_YMD_HM,
    ekDomain,
    ORDER_STATUS_CMS,
    ROUTES
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { Revenue } from '@/types/revenue';
import { formatDateTime } from '@/utils/datetime';
import { cn, scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { getMonth, getYear } from 'date-fns';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import FilterForm from './filter';
import Tooltip from '@/components/common/Tooltip';
import U20Icon from '@/assets/icons/u20-icon.png';
import { formatCurrency } from '@/utils/formatNumber';
import { isPathEk } from '@/utils/routes';

export default observer(function RevenueManagement() {
    // hook
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        locationStore: { getLocations },
        accountStore: { getAccounts },
        bikeStore: { getListModelName },
        revenueStore: {
            allRevenue,
            totalRecord,
            paging,
            totalRevenue,
            keepSearchParams,
            getAllRevenue,
            setObservable,
            exportExcelRevenues,
            exportPdfRevenues
        },
        modalStore: { hideAllModals }
    } = useStore();

    // state
    const [rowSelected, setRowSelected] = useState({});
    // const
    const currentYear = getYear(new Date());
    const currentMonth = getMonth(new Date()) + 1;

    // form
    const validateSchema = yup.object().shape({
        ownerId: yup.string(),
        locationId: yup.string(),
        year: yup.string(),
        month: yup.string(),
        bikeModel: yup.string().trim(),
        keyword: yup.string().trim(),
        isCancelBooking: yup.boolean().default(false).optional()
    });
    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            ownerId: keepSearchParams?.ownerId?.toString() ?? '',
            locationId: keepSearchParams?.locationId?.toString() ?? '',
            year: keepSearchParams?.year?.toString() ?? '',
            month: keepSearchParams?.month?.toString() ?? '',
            bikeModel: keepSearchParams?.bikeModel ?? '',
            keyword: keepSearchParams?.keyword ?? '',
            isCancelBooking: !!keepSearchParams?.isCancelBooking
        }
    });

    // lifecycle
    useEffect(() => {
        // fetch data form filter
        getAccounts({ isOwner: true, page: -1, size: 10 });
        getLocations({ page: -1, size: 10 });
        getListModelName();

        // fetch revenues
        onFetchData();
    }, []);

    // function
    const onFetchData = async () => {
        await flowResult(
            getAllRevenue(
                keepSearchParams || {
                    year: currentYear,
                    month: currentMonth,
                    isCancelBooking: false
                }
            )
        );

        if (!keepSearchParams) {
            setObservable(
                'keepSearchParams',
                { year: currentYear, month: currentMonth, isCancelBooking: false },
                { isMergeObject: false }
            );

            form.setValue('year', currentYear.toString());
            form.setValue('month', currentMonth.toString());
            form.setValue('isCancelBooking', false);
        }
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getAllRevenue(keepSearchParams, newPaging);
        scrollToTop();
    };

    const onSearch = (data: any) => {
        getAllRevenue(data, {
            ...paging,
            pagination: { ...paging.pagination, pageIndex: 0 }
        });
        setObservable('keepSearchParams', data, { isMergeObject: false });
        hideAllModals();
    };

    const handleClickView = (id) => {
        if (isEk) {
            navigate(
                `/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`
            );
        } else {
            navigate(
                `${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`
            );
        }
    };

    const onClickExportExcel = async () => {
        await flowResult(exportExcelRevenues({ ...keepSearchParams }));
    };

    const onClickExportPdf = async () => {
        await flowResult(exportPdfRevenues({ ...keepSearchParams }));
    };

    // const handleOpenFilter = () => {
    //     showModal({
    //         id: 'filter',
    //         size: 'lg',
    //         title: t('words_title.filter'),
    //         titleClassName: 'text-center w-full',
    //         footerClassName: 'p-0 m-0',
    //         content: (
    //             <FilterForm
    //                 form={form}
    //                 // isInModal={true}
    //                 onSearch={onSearch}
    //                 isOwner={isOwner}
    //             />
    //         )
    //     });
    // };

    // columns
    const columns: ColumnDef<Revenue>[] = [
        {
            id: 'id',
            accessorKey: 'id',
            header: '',
            size: 80,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <TableActionButtons
                    onView={() => handleClickView(original.id)}
                />
            )
        },
        {
            accessorKey: 'rentalFrom',
            header: t('field.reservation.rental_time'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='min-w-[114px] whitespace-nowrap'>
                    {formatDateTime(original?.rentalFrom || '', DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) || ''}
                </div>
            )
        },
        {
            accessorKey: 'rentalTo',
            header: t('field.reservation.return_time'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalTo || '', DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) || ''}
                </div>
            )
        },
        {
            accessorKey: 'location',
            header: t('field.location_name'),
            size: 250,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>{original?.location}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'bikeModel',
            header: t('field.model_bike'),
            size: 200,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[150px] line-clamp-2'>{original?.bikeModel}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'renterName',
            // header: t('field.renter_name'),
            header: t('field.customer_name'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex gap-1 justify-start min-w-[140px]'>
                    {original?.u20 && (
                        <Tooltip triggerClassName='min-w-6' content={t('sentences.under_20_age')}>
                            <img
                                src={U20Icon}
                                alt={`(${t('sentences.under_20_age')})`}
                                className='size-6'
                            />
                        </Tooltip>
                    )}
                    <p className='text-left break-all line-clamp-2'>
                        {original?.renterName || ''}
                    </p>
                </div>
            )
        },
        {
            accessorKey: 'insurance',
            header: t('field.vehicle_compensation'),
            size: 260,
            meta: {
                cellClassName: '',
                headerClassName: 'center-sub-header-item'
            },
            cell: ({ row: { original } }) => (
                <div className='text-center'>{original?.insurance}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'ownerName',
            header: t('field.owner'),
            // header: '所有者',
            size: 100,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original?.owner?.fullname ?? ''}
                </div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'owner.code',
            header: t('field.user_code'),
            size: 100,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original?.owner?.code ?? ''}
                </div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'paymentType',
            header: t('field.payment_method'),
            size: 100,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap min-w-[100px] line-clamp-2'>{original?.paymentType ?? ''}</div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'totalPayment',
            // header: t('words_title.revenue'),
            header: '合計金額',
            size: 200,
            enableSorting: false,
            meta: {
                cellClassName: '',
                headerClassName: 'right-sub-header-item'
            },
            cell: ({ row: { original } }) => (
                <div className='text-right'>
                    {t('currency.currency_symbol')}
                    {formatCurrency(original.totalPayment) || 0}
                </div>
            )
        },
        {
            accessorKey: 'status',
            header: t('field.reservation_status'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div
                    className={cn(
                        `${ORDER_STATUS_CMS[original?.status]?.color}`,
                        'font-medium min-w-[100px]'
                    )}
                >
                    {t(ORDER_STATUS_CMS[original?.status]?.key)}
                </div>
            )
        },
        {
            accessorKey: 'orderCodeSystem',
            header: t('field.reservation_code'),
            size: 260,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    {original?.orderCodeSystem || ''}
                </div>
            )
        }
    ];

    return (
        <>
            <div className='layout-card'>
                <FilterForm form={form} onSearch={onSearch} />
            </div>
            <div className='layout-card mt-6'>
                {/* <div className='flex justify-start items-center gap-3 mb-5'>
                    <div className='inline-block px-2 py-1 border-[2px] border-title-table text-title-table leading-[1] font-semibold rounded-[8px]'>TOTAL</div>
                    <span className='text-fs-mid18 text-title-table leading-[1] font-semibold'>
                        {t('currency.currency_symbol')}
                        {formatCurrency(totalRevenue) ?? '0'}
                    </span>
                </div> */}
                <TableCustomOnlyTwoDept
                    columns={columns}
                    data={allRevenue}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    headerClassName='justify-start items-center'
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    onRowSelectionChange={setRowSelected}
                    getRowId={row => row.id?.toString()}
                    renderTotal={(
                        <div className='flex justify-start items-center gap-3'>
                            <div className='inline-block px-2 py-1 border-[2px] border-title-table text-title-table leading-[1] font-semibold rounded-[8px]'>TOTAL</div>
                            <span className='text-fs-mid18 text-title-table leading-[1] font-semibold'>
                                {t('currency.currency_symbol')}
                                {formatCurrency(totalRevenue) ?? '0'}
                            </span>
                        </div>
                    )}
                    renderActions={(
                        <div className='flex gap-4'>
                            <ButtonCusWithIcon
                                type='button'
                                variant='default'
                                size='md'
                                // className='h-9.5 md:h-12'
                                onClick={() => onClickExportPdf()}
                                iconId='#icon-pdf'
                                widthIcon={16}
                                heightIcon={18}
                            >
                                {t('button.export_to_pdf')}
                            </ButtonCusWithIcon>
                            <ButtonCusWithIcon
                                type='button'
                                variant='green'
                                size='md'
                                // className='h-9.5 md:h-12'
                                onClick={() => onClickExportExcel()}
                                iconId='#icon-excel'
                                widthIcon={20}
                                heightIcon={20}
                            >
                                {t('button.export_to_excel')}
                            </ButtonCusWithIcon>
                        </div>
                    )}
                />
            </div>
        </>
    );
});
